import {
  OktonLoadingCard,
  OktonPageHeader,
  OktonInput,
  OktonAlert,
  OktonEmptyState,
} from '@okton/platform-ui';
import {
  Box,
  Button,
  Flex,
  SimpleGrid,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
} from '@chakra-ui/react';
import { Check } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import BankAppPage from '../../../layout/BankAppPage';
import {
  podeCompensar,
  useCompensarDeposito,
  useDepositos,
} from '../../../services/hooks/useDepositos';
import { formatCustomDate } from '../../../utils/formatCustomDate';
import formatValue from '../../../utils/formatValue';

// Data de hoje no formato do <input type="date">, no fuso local: toISOString
// devolveria UTC e viraria o dia anterior à noite.
function hoje(): string {
  const agora = new Date();
  const mes = String(agora.getMonth() + 1).padStart(2, '0');
  const dia = String(agora.getDate()).padStart(2, '0');
  return `${agora.getFullYear()}-${mes}-${dia}`;
}

function agoraHora(): string {
  const agora = new Date();
  return `${String(agora.getHours()).padStart(2, '0')}:${String(
    agora.getMinutes(),
  ).padStart(2, '0')}`;
}

/**
 * Compensação de um Depósito: o banco confirmou que o dinheiro entrou.
 *
 * Não move Caixa — quem moveu foi o cadastro do Depósito. O que muda aqui é o
 * status e a data em que o banco compensou, que é a mesma informação que a
 * tela de Depósito do IFLEX pede no F7.
 */
const CompensarDeposito = () => {
  const history = useHistory();
  const bg = useColorModeValue('white', 'gray.800');
  const subtleBg = useColorModeValue('gray.50', 'gray.700');
  const color = useColorModeValue('#3f3f46', '#d4d4d8');
  const { idDeposito } = useParams<{ idDeposito: string }>();

  // 'all' e sem recorte de data: o Depósito a compensar pode ser de qualquer
  // período, e a tela chega aqui por link direto.
  const { data: depositos = [], isLoading } = useDepositos(undefined, '', '');
  const compensarMutation = useCompensarDeposito();

  // O IFLEX abre o formulário já com a data de hoje; a hora acompanha, que é o
  // caso comum de quem compensa na hora em que o banco confirma.
  const [form, setForm] = useState({ data: hoje(), hora: agoraHora() });
  const [erro, setErro] = useState('');

  const deposito = depositos.find(d => d.id_dep === idDeposito);

  // A lista chega depois do primeiro render: sem isto a tela decidiria pelo
  // Depósito antes de conhecê-lo.
  useEffect(() => {
    if (!deposito) return;
    if (!podeCompensar(deposito)) {
      setErro('Este Depósito não está Pendente e não pode ser compensado.');
    }
  }, [deposito]);

  const salvar = async () => {
    if (!form.data) {
      setErro('Informe a data da compensação.');
      return;
    }
    setErro('');
    try {
      await compensarMutation.mutateAsync({
        deposito: idDeposito,
        data: form.data,
        hora: form.hora,
      });
      history.push('/depositos');
    } catch (err) {
      setErro(
        err instanceof Error ? err.message : 'Erro ao compensar o Depósito.',
      );
    }
  };

  const bloqueado = !!deposito && !podeCompensar(deposito);

  return (
    <BankAppPage>
      <OktonPageHeader
        eyebrow="CADASTROS"
        title="Compensar Depósito"
        subtitle="Data e hora em que o banco confirmou o depósito"
        onBack={() => history.push('/depositos')}
      />

      <Box bg={bg} mt="4" borderWidth="1px" rounded="2xl" p="6">
        {isLoading ? (
          <OktonLoadingCard h="200px" />
        ) : (
          <Stack spacing="4" maxW="720px">
            {!!erro && <OktonAlert status="error" description={erro} />}

            {!deposito ? (
              <OktonEmptyState
                title="Depósito não encontrado."
                description="Ele pode ter sido excluído — volte e atualize a lista."
              />
            ) : (
              <>
                {/* O que está sendo compensado, para não depender da memória
                de quem clicou na linha. */}
                <Box bg={subtleBg} borderRadius="lg" px="4" py="3">
                  <Text fontSize="xs" color="gray.500">
                    Depósito
                  </Text>
                  <Flex align="center" gridGap="2" mt="1" flexWrap="wrap">
                    <Text fontWeight="semibold" color={color}>
                      {formatValue(Number(deposito.valor_dep))}
                    </Text>
                    <Text fontSize="sm" color="gray.500">
                      em{' '}
                      {formatCustomDate(
                        deposito.data_dep.slice(0, 10),
                        'yyyy-MM-dd',
                        'dd/MM/yyyy',
                      )}
                      {deposito.descricao_cts
                        ? ` — ${deposito.descricao_cts}`
                        : ''}
                    </Text>
                  </Flex>
                  {!!deposito.envelope_dep && (
                    <Text fontSize="xs" color="gray.500" mt="1">
                      Envelope {deposito.envelope_dep}
                    </Text>
                  )}
                </Box>

                <SimpleGrid columns={[1, 2]} spacing="4">
                  <Box>
                    <Text
                      as="label"
                      htmlFor="compensacao-deposito-data"
                      display="block"
                      fontSize="sm"
                      fontWeight="medium"
                      mb="1"
                    >
                      Data da compensação *
                    </Text>
                    <OktonInput
                      name="compensacao-deposito-data"
                      type="date"
                      size="lg"
                      rounded="2xl"
                      fontWeight="light"
                      color={color}
                      value={form.data}
                      onChange={e => {
                        const valor = e.target.value;
                        setForm(f => ({ ...f, data: valor }));
                      }}
                    />
                  </Box>
                  <Box>
                    <Text
                      as="label"
                      htmlFor="compensacao-deposito-hora"
                      display="block"
                      fontSize="sm"
                      fontWeight="medium"
                      mb="1"
                    >
                      Hora
                    </Text>
                    <OktonInput
                      name="compensacao-deposito-hora"
                      type="time"
                      size="lg"
                      rounded="2xl"
                      fontWeight="light"
                      color={color}
                      value={form.hora}
                      onChange={e => {
                        const valor = e.target.value;
                        setForm(f => ({ ...f, hora: valor }));
                      }}
                    />
                    <Text fontSize="xs" color="gray.500" mt="1">
                      Em branco, o IFLEX grava meia-noite.
                    </Text>
                  </Box>
                </SimpleGrid>

                <Text fontSize="xs" color="gray.500">
                  Compensar não movimenta o Caixa: quem movimentou foi o
                  cadastro do Depósito. O que muda é a situação e a data em que
                  o banco confirmou.
                </Text>
              </>
            )}

            <Flex gridGap="3" pt="2">
              <Button
                variant="ghost"
                onClick={() => history.push('/depositos')}
              >
                Cancelar
              </Button>
              <Button
                colorScheme="blue"
                leftIcon={
                  compensarMutation.isLoading ? (
                    <Spinner size="sm" />
                  ) : (
                    <Check size={16} />
                  )
                }
                isDisabled={
                  compensarMutation.isLoading || !deposito || bloqueado
                }
                onClick={salvar}
              >
                Compensar
              </Button>
            </Flex>
          </Stack>
        )}
      </Box>
    </BankAppPage>
  );
};

export default CompensarDeposito;
